/**
 * ArchiveClientModal — подтверждение архивации клиента (ADMIN_SPEC §9,
 * опасное действие). Причина уезжает в аудит вместе с POST /archive;
 * после успеха список перечитывается, дравер закрывается.
 */

import {useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {TriangleAlert} from 'lucide-react';

import {archiveClient} from '@services/clients';
import {keys} from '@queries/keys';
import {Field} from '@components/FormKit';

import {ModalShell} from '../ModalShell';

type Props = {
  open: boolean;
  clientId: string;
  clientName: string;
  onClose: () => void;
  /** Успех: закрыть модалку и дравер, показать тост */
  onArchived: () => void;
};

export const ArchiveClientModal = ({open, clientId, clientName, onClose, onArchived}: Props) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');

  const [prevOpen, setPrevOpen] = useState(false);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setReason('');
  }

  const mutation = useMutation({
    mutationFn: archiveClient,
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      onArchived();
    },
  });

  const handleSubmit = () => mutation.mutate({id: clientId, reason: reason.trim() || undefined});

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Архивировать клиента"
      subtitle={
        <>
          <span className="font-semibold text-foreground">«{clientName}»</span> потеряет доступ к
          кабинету, подписка остановится. Действие видно в аудите.
        </>
      }
      submitLabel="Архивировать"
      onSubmit={handleSubmit}
      pending={mutation.isPending}
      error={mutation.isError ? 'Не удалось архивировать. Попробуйте ещё раз' : null}
      danger
    >
      <div className="flex gap-2.5 rounded-[11px] border border-destructive/25 bg-destructive/5 p-[11px_13px]">
        <TriangleAlert className="mt-px size-[17px] shrink-0 text-destructive" strokeWidth={1.9} />
        <div className="text-xs leading-[1.45] text-skr-ink2">
          Обратной кнопки в панели нет — восстановление только через бэкенд. Убедитесь, что клиент
          предупреждён.
        </div>
      </div>
      <Field label="Причина (попадёт в аудит)">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, 500))}
          placeholder="Например: клиника закрылась, попросили остановить подписку"
          rows={3}
          className="w-full resize-none rounded-[10px] border border-input bg-card p-[11px_13px] text-sm leading-[1.5] outline-none transition focus:border-primary focus:ring-[3px] focus:ring-primary/12"
        />
      </Field>
    </ModalShell>
  );
};
